// src/App.js
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense } from "react";
import Layout from "./components/layout/Layout";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import CorsNotification from "./components/CorsNotification/CorsNotification";

// Admin pages
import Organization from "./pages/Organization/Organization";
import QuestionBank from "./pages/QuestionBank/QuestionBank";
import Questionnaire from "./pages/Questionnaire/Questionnaire";
import QuestionnaireEditor from "./pages/Questionnaire/Editor/QuestionnaireEditor";

// User pages
import MyAssignments from "./user/pages/MyAssignments";
import AssignmentPlayer from "./user/pages/AssignmentPlayer";
import Profile from "./user/pages/Profile";

// Auth
import LoginPage from "./pages/auth/LoginPage";
import AccessPage from "./pages/auth/AccessPage";
import ProtectedRoute from "./router/ProtectedRoute";

// Global error modal (usa ModalShell + errorBus)
import GlobalErrorModal from "./components/errors/GlobalErrorModal";
import GlobalNotifications from "./components/notifications/GlobalNotifications";
import "./components/Modal/TeamAssessForms.css";

function HomeRedirect() {
  const { roles, loading } = useAuth();
  if (loading) return null;
  const isAdmin = Array.isArray(roles) && roles.includes("ADMIN");
  return <Navigate to={isAdmin ? "/organization" : "/assignments"} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/access" element={<AccessPage />} />

              {/* Protected shell */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Root → redirección por rol */}
                <Route index element={<HomeRedirect />} />

                {/* Rutas ADMIN */}
                <Route
                  path="organization/*"
                  element={
                    <ProtectedRoute
                      allowRoles={["ADMIN"]}
                      fallback="/assignments"
                    >
                      <Organization />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="question-bank/*"
                  element={
                    <ProtectedRoute
                      allowRoles={["ADMIN"]}
                      fallback="/assignments"
                    >
                      <QuestionBank />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="questionnaire/*"
                  element={
                    <ProtectedRoute
                      allowRoles={["ADMIN"]}
                      fallback="/assignments"
                    >
                      <Questionnaire />
                    </ProtectedRoute>
                  }
                />

                {/* Rutas de usuario (sin /app) */}
                <Route path="assignments" element={<MyAssignments />} />
                <Route
                  path="assignments/:assignmentId"
                  element={<AssignmentPlayer />}
                />
                <Route path="profile" element={<Profile />} />
              </Route>

              {/* Editor individual solo ADMIN */}
              <Route
                path="/questionnaires/editor/:id"
                element={
                  <ProtectedRoute
                    allowRoles={["ADMIN"]}
                    fallback="/assignments"
                  >
                    <QuestionnaireEditor />
                  </ProtectedRoute>
                }
              />

              {/* 404 → al home para redirección por rol */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            {/* UI global */}
            <CorsNotification />
            <GlobalErrorModal />
            <GlobalNotifications />
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
